import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {ButtonModule} from 'primeng/button';
import {DropdownModule} from 'primeng/dropdown';
import {InputTextModule} from 'primeng/inputtext';
import {SelectItem} from 'primeng/api';
import {of, Subject, Subscription, timer} from 'rxjs';
import {catchError, map, switchMap, takeUntil} from 'rxjs/operators';
import {EtlI18nConversionService} from '../etl-i18n-conversion.service';
import {emptyI18nConversion, I18nConversionConfig, I18nConversionGroup, I18nConversionPreview} from '../i18n-conversion.interface';

@Component({
    selector: 'dmp-etl-i18n-conversion',
    standalone: true,
    imports: [CommonModule, FormsModule, TranslateModule, ButtonModule, DropdownModule, InputTextModule],
    templateUrl: './etl-i18n-conversion.component.html',
    styleUrls: ['./etl-i18n-conversion.component.scss']
})
export class EtlI18nConversionComponent implements OnChanges, OnInit, OnDestroy {
    @Input() tenant: string;
    @Input() configuration: I18nConversionConfig;
    @Input() examples: string | string[];
    @Input() inputId = 'i18n-conversion';
    @Output() configurationChange = new EventEmitter<I18nConversionConfig>();
    @Output() validityChange = new EventEmitter<boolean>(true);

    draft = emptyI18nConversion();
    groups: SelectItem[] = [];
    fields: SelectItem[] = [];
    policies: SelectItem[] = [];
    languages: SelectItem[] = [];
    previewRows: I18nConversionPreview[] = [];
    testValue = '';
    needsLanguage = false;
    valid = false;
    loadingGroups = false;
    groupsError = false;
    loadingPreview = false;
    previewError = false;
    private catalog: I18nConversionGroup[] = [];
    private loadedTenant: string;
    private groupSubscription: Subscription;
    private readonly destroyed = new Subject<void>();
    private readonly previewRequests = new Subject<{tenant: string; config: I18nConversionConfig; sources: string[]} | null>();

    constructor(private conversion: EtlI18nConversionService, private translate: TranslateService) {}

    ngOnChanges(): void {
        this.draft = this.conversion.normalize(this.tenant, this.configuration || emptyI18nConversion());
        if (this.loadedTenant !== this.tenant) {
            this.loadedTenant = this.tenant;
            this.testValue = '';
            this.loadGroups();
        }
        this.refresh();
    }

    ngOnInit(): void {
        this.previewRequests.pipe(
            // Cancel stale requests immediately, including while waiting for the typing debounce.
            switchMap(request => request ? timer(250).pipe(
                switchMap(() => this.conversion.preview(request.tenant, request.config, request.sources)),
                map(rows => ({rows, error: false})),
                catchError(() => of({rows: [], error: true}))
            ) : of({rows: [], error: false})),
            takeUntil(this.destroyed)
        ).subscribe(result => {
            this.previewRows = result.rows;
            this.previewError = result.error;
            this.loadingPreview = false;
        });
        this.translate.onLangChange.pipe(takeUntil(this.destroyed)).subscribe(() => this.refresh());
        this.updatePreview();
    }

    ngOnDestroy(): void {
        this.groupSubscription?.unsubscribe();
        this.destroyed.next();
        this.destroyed.complete();
    }

    loadGroups(reload = false): void {
        this.groupSubscription?.unsubscribe();
        this.catalog = [];
        this.loadingGroups = !!this.tenant;
        this.groupsError = !this.tenant;
        if (!this.tenant) return;
        this.groupSubscription = this.conversion.loadGroups(this.tenant, reload).pipe(takeUntil(this.destroyed)).subscribe({
            next: groups => {
                this.catalog = groups;
                this.loadingGroups = false;
                this.groupsError = false;
                this.refresh();
            },
            error: () => {
                this.loadingGroups = false;
                this.groupsError = true;
                this.refresh();
            }
        });
    }

    onGroupChange(): void {
        this.testValue = '';
        const group = this.conversion.getGroup(this.tenant, this.draft.group);
        if (!group?.fields.some(field => field.value === this.draft.matchField)) {
            this.draft.matchField = group?.defaultMatchField ?? null;
        }
        if (!group?.fields.some(field => field.value === this.draft.resultField)) this.draft.resultField = null;
        if (!group?.languages.includes(this.draft.language)) delete this.draft.language;
        this.onSelectionChange();
    }

    onSelectionChange(): void {
        if (this.conversion.needsLanguage(this.tenant, this.draft) && !this.draft.language) {
            const languages = this.conversion.getGroup(this.tenant, this.draft.group)?.languages || [];
            this.draft.language = languages.includes('FR') ? 'FR' : languages[0];
        }
        this.commit();
    }

    updatePreview(): void {
        const supplied = this.testValue.trim() ? [this.testValue] :
            (Array.isArray(this.examples) ? this.examples : (this.examples || '').split(/[,;\n]/));
        const sources = Array.from(new Set(supplied.map(value => value?.trim()).filter(Boolean))).slice(0, 5);
        this.previewRows = [];
        this.previewError = false;
        this.loadingPreview = this.valid && sources.length > 0;
        this.previewRequests.next(this.loadingPreview ? {tenant: this.tenant, config: {...this.draft}, sources} : null);
    }

    previewMessage(row: I18nConversionPreview): string {
        return row.status === 'NOT_FOUND' ? `etlI18nConversion.preview.noMatch.${this.draft.onNoMatch}` :
            `etlI18nConversion.preview.status.${row.status}`;
    }

    previewIsError(row: I18nConversionPreview): boolean {
        return row.status === 'AMBIGUOUS' || row.status === 'MISSING_RESULT' ||
            (row.status === 'NOT_FOUND' && this.draft.onNoMatch === 'ERROR');
    }

    private commit(): void {
        this.draft = this.conversion.normalize(this.tenant, this.draft);
        this.refresh();
        this.configurationChange.emit({...this.draft});
    }

    private refresh(): void {
        this.groups = this.catalog.map(group => ({value: group.value, label: this.translate.instant(group.label)}));
        const group = this.conversion.getGroup(this.tenant, this.draft.group);
        this.fields = (group?.fields || []).map(field => ({value: field.value, label: this.translate.instant(field.label)}));
        const languageNames = {FR: 'Français', EN: 'English', IT: 'Italiano', DE: 'Deutsch', NL: 'Nederlands', ES: 'Español'};
        this.languages = (group?.languages || []).map(value => ({value, label: languageNames[value]}));
        this.policies = this.conversion.noMatchPolicies.map(policy =>
            ({value: policy, label: this.translate.instant(`etlI18nConversion.policies.${policy}`)}));
        this.needsLanguage = this.conversion.needsLanguage(this.tenant, this.draft);
        this.valid = !this.loadingGroups && !this.groupsError && this.conversion.isValid(this.tenant, this.draft);
        this.validityChange.emit(this.valid);
        this.updatePreview();
    }
}
